// Admin landing — Wave E follow-up.
//
// Two groups of card links: People (Users, Drivers) and Permissions (Roles).
// Visible to tenant admin only (router-gated).

import { Link } from 'react-router-dom';
import { ShieldCheck, Smartphone, Truck, Users } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import styles from './AdminIndexPage.module.css';

interface AdminCard {
  to: string;
  label: string;
  description: string;
  icon: ComponentType<LucideProps>;
}

interface AdminGroup {
  title: string;
  items: AdminCard[];
}

const GROUPS: AdminGroup[] = [
  {
    title: 'People',
    items: [
      {
        to: '/admin/users',
        label: 'Users',
        description: 'Invite, edit, and deactivate staff accounts.',
        icon: Users,
      },
      {
        to: '/admin/drivers',
        label: 'Drivers',
        description: 'Manage Dispatch driver logins and contact details.',
        icon: Truck,
      },
      {
        to: '/admin/drivers/devices',
        label: 'Driver devices',
        description: 'Which app version each driver runs, push status and last location.',
        icon: Smartphone,
      },
    ],
  },
  {
    title: 'Permissions',
    items: [
      {
        to: '/admin/roles',
        label: 'Roles',
        description: 'Read-only matrix of what each role can do.',
        icon: ShieldCheck,
      },
    ],
  },
];

export function AdminIndexPage(): JSX.Element {
  return (
    <div className={styles.page}>
      <PageHeader
        title="Admin"
        subtitle="Manage people and permissions for your company"
      />

      {GROUPS.map((group) => (
        <Card key={group.title} title={group.title}>
          <ul className={styles.grid}>
            {group.items.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className={styles.card}>
                  <span className={styles.icon}>
                    <item.icon size={20} aria-hidden />
                  </span>
                  <span className={styles.text}>
                    <span className={styles.label}>{item.label}</span>
                    <span className={styles.description}>{item.description}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
